import type { ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import { roundAndFormat } from '~/helper/number';

export const VALIANT_COMPANY_ID = '145859503298';

type WP = Record<string, any> | undefined; // eslint-disable-line @typescript-eslint/no-explicit-any

export function V3ResultCalc() {
  const { t } = useTranslate();
  return (
    <div
      className="v3p-page"
      aria-busy="true"
      aria-label={t('v3_result_calc_aria', 'Bewertung wird berechnet')}
    >
      <section
        className="v3-card"
        data-od-id="calc"
      >
        <span className="v3-chip c-info">
          {t('v3_result_calc_chip', 'Wird berechnet')}
        </span>
        <h1
          className="v3-head"
          style={{ fontSize: '1.5rem', fontWeight: 700 }}
        >
          {t('v3_result_calc_title', 'Wir berechnen Ihre Bewertung…')}
        </h1>
        <p className="v3p-mu">
          {t(
            'v3_result_calc_text',
            'Das dauert meist weniger als eine Minute. Sie können dieses Fenster offen lassen, die Seite aktualisiert sich selbst.',
          )}
        </p>
        <ol className="v3p-steps">
          <li className="done">
            <b>{t('v3_result_calc_step_address', 'Adresse geprüft')}</b>
          </li>
          <li className="now">
            <b>{t('v3_result_calc_step_market', 'Marktdaten laden')}</b>
          </li>
          <li>
            <b>{t('v3_result_calc_step_value', 'Wert berechnen')}</b>
          </li>
        </ol>
        <div
          className="v3p-sk"
          style={{ width: '70%' }}
        />
        <div
          className="v3p-sk"
          style={{ width: '45%' }}
        />
      </section>
    </div>
  );
}

export function V3ResultError(props: {
  readonly title: string;
  readonly children: ReactNode;
  readonly action?: {
    readonly label: string;
    readonly href?: string;
    readonly onClick?: () => void;
  };
}) {
  const { t } = useTranslate();
  const a = props.action;
  return (
    <div className="v3p-page">
      <section
        className="v3-card"
        role="alert"
        data-od-id="result-error"
      >
        <span className="v3-chip c-bad">
          {t('v3_result_error_chip', 'Bewertung nicht möglich')}
        </span>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>{props.title}</h1>
        <div className="v3p-mu v3p-sm">{props.children}</div>
        <div className="v3p-row">
          {a?.href ? (
            <a
              className="v3-btn pri"
              href={a.href}
              data-track="immodossier valiant result error back link"
            >
              {a.label}
            </a>
          ) : a?.onClick ? (
            <button
              type="button"
              className="v3-btn pri"
              onClick={a.onClick}
              data-track="immodossier valiant result error back button"
            >
              {a.label}
            </button>
          ) : null}
          <a
            className="v3-btn ghost"
            href={`mailto:${t('v3_support_email', 'support@agentselly.ch')}`}
            data-track="immodossier valiant result support mailto"
          >
            {t('v3_result_error_support', 'Support kontaktieren')}
          </a>
        </div>
      </section>
    </div>
  );
}

function V3RangeMeter(props: {
  readonly min: number;
  readonly value: number;
  readonly max: number;
}) {
  const { t } = useTranslate();
  const pad = (props.max - props.min) * 0.25;
  const range = (props.max - props.min) * 1.5;
  const pc = (x: number) =>
    Math.round(((x - props.min + pad) / range) * 1000) / 10;
  const f = (x: number) => `CHF ${roundAndFormat(x)}`;
  return (
    <div
      className="v3p-rng"
      role="img"
      aria-label={t(
        'v3_result_range_aria',
        'Schätzwert {value} innerhalb {min} bis {max}',
        { value: f(props.value), min: f(props.min), max: f(props.max) },
      )}
    >
      <span
        className="v3p-rng-v v3p-num"
        style={{ left: `${pc(props.value)}%` }}
      >
        {f(props.value)}
      </span>
      <span className="v3p-rng-t">
        <i
          className="v3p-rng-b"
          style={{
            left: `${pc(props.min)}%`,
            width: `${pc(props.max) - pc(props.min)}%`,
          }}
        />
        <i
          className="v3p-rng-d"
          style={{ left: `${pc(props.value)}%` }}
        />
      </span>
      <span
        className="v3p-rng-l v3p-num"
        style={{ left: `${pc(props.min)}%` }}
      >
        {f(props.min)}
      </span>
      <span
        className="v3p-rng-l v3p-num"
        style={{ left: `${pc(props.max)}%` }}
      >
        {f(props.max)}
      </span>
    </div>
  );
}

export function V3ResultPage(props: {
  readonly widgetProps: WP;
  readonly companyId?: string;
  readonly dossierCreated?: boolean;
}) {
  const { t } = useTranslate();
  const wp = props.widgetProps ?? {};
  const welcome = wp.welcome ?? {};
  const ev = wp.evaluation ?? {};
  const info = wp.evaluationPropertyInformation ?? {};
  const offer = wp.offer ?? {};
  const blog = wp.blog ?? {};
  const valiant = props.companyId === VALIANT_COMPANY_ID;
  const posts: { title: string; link: string }[] = blog.blogPosts ?? [];
  const hasValue =
    ev.evaluationValue !== null && ev.evaluationValue !== undefined;
  const salutation = welcome.lastName
    ? `${welcome.isFemale ? t('salutation_mrs') : t('salutation_mr')} ${
        welcome.lastName
      }`
    : '';
  const address: string = info.fullAddress ?? info.address ?? '';
  const min: number | undefined = ev.evaluationMin ?? ev.lowerLimit;
  const max: number | undefined = ev.evaluationMax ?? ev.upperLimit;
  const evalDate: string | undefined = ev.evaluationDate ?? ev.timestamp;
  const stand = evalDate ? new Date(evalDate) : null;
  const kv: [string, unknown, string?][] = [
    [
      'v3_result_info_rooms',
      info.numberOfRooms,
    ],
    [
      'v3_result_info_area',
      info.livingArea,
      ' m²',
    ],
    [
      'v3_result_info_year',
      info.buildingYear,
    ],
    [
      'v3_result_info_baths',
      info.numberOfBathrooms,
    ],
    [
      'v3_result_info_land',
      info.landArea,
      ' m²',
    ],
    [
      'v3_result_info_balcony',
      info.balconyArea,
      ' m²',
    ],
  ];
  const labels: Record<string, string> = {
    v3_result_info_rooms: 'Zimmer',
    v3_result_info_area: 'Wohnfläche',
    v3_result_info_year: 'Baujahr',
    v3_result_info_baths: 'Badezimmer',
    v3_result_info_land: 'Grundstück',
    v3_result_info_balcony: 'Balkon',
  };

  return (
    <div className="v3p-page">
      {valiant && (
        <div
          className="v3p-vbar"
          data-od-id="valiant-bar"
        >
          <span
            className="v3p-vlg"
            aria-hidden="true"
          >
            V
          </span>
          <b>Valiant</b>
          <span className="v3p-mu v3p-sm">
            {t('v3_valiant_bar_text', 'in Zusammenarbeit mit AgentSelly')}
          </span>
        </div>
      )}
      <div
        className="v3-head"
        data-od-id="page-head"
      >
        <h1>
          {t('welcome_greetings')} {salutation}
        </h1>
        {address && (
          <p>
            {t('v3_result_subtitle', 'Hier ist die Bewertung für {address}.', {
              address,
            })}
          </p>
        )}
      </div>
      {props.dossierCreated && (
        <div
          className="v3-okbox"
          role="status"
          data-od-id="dossier-created"
        >
          <span>
            <b>
              {t('v3_result_created_title', 'Ihr ImmoDossier wurde angelegt.')}
            </b>{' '}
            {t(
              'v3_result_created_text',
              'Mit der Anmeldung sehen Sie Zustand, Region und Markt Ihrer Immobilie.',
            )}
          </span>
        </div>
      )}
      <div className="v3p-grid split">
        <section
          className="v3-card"
          data-od-id="evaluation"
          aria-labelledby="h-ev"
        >
          <div className="v3p-sec">
            <h2 id="h-ev">{t('nav_evaluation')}</h2>
            {stand && !Number.isNaN(stand.getTime()) && (
              <span className="v3p-xs v3p-mu">
                {t('v3_result_stand', 'Stand {date}', {
                  date: stand.toLocaleDateString('de-CH'),
                })}
              </span>
            )}
          </div>
          {hasValue ? (
            <>
              <p className="v3p-big v3p-num">
                CHF {roundAndFormat(ev.evaluationValue)}
              </p>
              {min !== undefined && max !== undefined && max > min && (
                <V3RangeMeter
                  min={min}
                  value={ev.evaluationValue}
                  max={max}
                />
              )}
            </>
          ) : (
            <p className="v3p-mu">
              {t('v3_result_no_value', 'Kein Wert verfügbar.')}
            </p>
          )}
          <p className="v3p-xs v3p-mu">
            {t(
              'v3_result_disclaimer',
              'Statistische Schätzung. Für eine verbindliche Einschätzung empfehlen wir eine Besichtigung durch unsere Berater.',
            )}
          </p>
          {ev.link && (
            <a
              className="v3-btn pri"
              href={`${ev.link}#evaluationDetailedEvaluation`}
              data-track="immodossier valiant evaluation detail link"
            >
              {t('widget_evaluation_button_text')}
            </a>
          )}
        </section>
        <section
          className="v3-card"
          data-od-id="next-steps"
          aria-labelledby="h-ns"
        >
          <h2 id="h-ns">{t('v3_result_next_title', 'Nächste Schritte')}</h2>
          <ol className="v3p-steps">
            <li className="done">
              <b>{t('v3_result_next_1', 'Bewertung erhalten')}</b>
            </li>
            <li>
              <b>{t('v3_result_next_2', 'Zustand ergänzen')}</b>
              <span className="v3p-xs v3p-mu">
                {t('v3_result_next_2_sub', 'Genauerer Wert in 2 Minuten')}
              </span>
            </li>
            <li>
              <b>{t('v3_result_next_3', 'Beratungsgespräch')}</b>
              <span className="v3p-xs v3p-mu">
                {t('v3_result_next_3_sub', 'Unverbindlich und kostenlos')}
              </span>
            </li>
          </ol>
        </section>
      </div>
      <section
        className="v3-card"
        data-od-id="property-info"
        aria-labelledby="h-pi"
      >
        <h2 id="h-pi">{t('v3_result_info_title', 'Ihre Angaben')}</h2>
        <dl className="v3-kv">
          {kv
            .filter(
              ([
                ,
                v,
              ]) => v !== null && v !== undefined && v !== '',
            )
            .map(
              ([
                k,
                v,
                unit,
              ]) => (
                <div key={k}>
                  <dt>{t(k, labels[k])}</dt>
                  <dd className="v3p-num">{`${v}${unit ?? ''}`}</dd>
                </div>
              ),
            )}
        </dl>
      </section>
      <div className="v3p-grid split">
        <section
          className={`v3-card${valiant ? ' v3p-vbrand' : ''}`}
          data-od-id="offer"
          aria-labelledby="h-of"
        >
          <h2 id="h-of">{t('widget_offer_title')}</h2>
          <p className="v3p-mu">{t('widget_offer_description')}</p>
          <div className="v3p-row">
            {offer.offerWebsiteURL && (
              <a
                className={`v3-btn ${valiant ? 'vbtn' : 'pri'}`}
                href={offer.offerWebsiteURL}
                target="_blank"
                rel="noreferrer"
                data-track="immodossier valiant offer contact link"
              >
                {t('widget_offer_button_offer')}
              </a>
            )}
            {offer.benefitsWebsiteURL && (
              <a
                className="v3-btn ghost v3p-hide-s"
                href={offer.benefitsWebsiteURL}
                target="_blank"
                rel="noreferrer"
                data-track="immodossier valiant offer benefits link"
              >
                {t('widget_offer_button_benefits')}
              </a>
            )}
          </div>
        </section>
        <section
          className="v3-card"
          data-od-id="blog"
          aria-labelledby="h-bl"
        >
          <h2 id="h-bl">{t(blog.title || 'widget_blog_title')}</h2>
          {posts.length ? (
            <ul className="v3p-posts">
              {posts.map((p) => (
                <li key={p.link}>
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {p.title}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="v3p-mu">{t('empty_no_blog_posts')}</p>
          )}
        </section>
      </div>
    </div>
  );
}
